import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BarChart3, Mail, Search, X, Users, Building2, UserCheck, List, Plus } from 'lucide-react';
import type { TemplateDefinition, RecipientType, CustomRecipient, SelectedCommittee } from '../../../types/outreach';
import RichTextEditor from '../../RichTextEditor';
import api from '../../../lib/api';
import HelpTrigger from '../../../help/components/HelpTrigger';
import { RESPONDENT_INFO_FIELD_OPTIONS } from '../RecipientContactCard';

const DEFAULT_EMAIL_TEMPLATE: NonNullable<NonNullable<TemplateDefinition['legislator_contact']>['email_template']> = {
  subject: { content: 'Constituent Feedback on {{bill.number}}' },
  opening: { content: 'Dear {{recipient.name}},\n\nI am writing as your constituent to share my perspective on {{bill.number}} - {{bill.title}}.' },
  included_question_ids: [],
  free_text_enabled: true,
  free_text_placeholder: 'Add your personal message here...',
  closing: { content: 'Thank you for your time and consideration.\n\nSincerely,\n{{respondent.first_name}} {{respondent.last_name}}' },
};

const RECIPIENT_TYPE_OPTIONS: { value: RecipientType; label: string; icon: React.FC<{ className?: string }>; description: string; requirement?: string }[] = [
  { value: 'house_rep', label: 'House Representative', icon: Building2, description: "Respondent's House Rep", requirement: 'district' },
  { value: 'senate_rep', label: 'Senate Representative', icon: Building2, description: "Respondent's Senate Rep", requirement: 'district' },
  { value: 'committee_members', label: 'Committee Members', icon: Users, description: 'Members of related bill committee(s)', requirement: 'bill' },
  { value: 'committee', label: 'Specific Committees', icon: Users, description: 'Members of committees you choose' },
  { value: 'candidate', label: 'Related Candidate', icon: UserCheck, description: 'Candidate linked via Related To', requirement: 'candidate' },
  { value: 'custom', label: 'Custom Distribution', icon: List, description: 'Search existing people or add any email address' },
];

/** Keep in sync with MAX_COMMITTEES in backend/src/utils/validators/outreachValidators.js */
const MAX_COMMITTEES = 25;

interface CommitteeOption extends SelectedCommittee {
  member_count: number;
  contactable_count: number;
}

interface Props {
  definition: TemplateDefinition;
  onChange: (definition: TemplateDefinition) => void;
}

const WizardStep4Settings: React.FC<Props> = ({ 
  definition, 
  onChange
}) => {
  const hasDistrict = [
    ...(definition.demographics?.required_fields || []),
    ...(definition.demographics?.optional_fields || [])
  ].includes('district');

  const hasBillLink = (definition.entity_links || []).some(l => l.entity_type === 'bill');
  const hasCandidateLink = (definition.entity_links || []).some(l => l.entity_type === 'candidate');

  const recipientTypes = definition.legislator_contact?.recipient_types || [];
  const customRecipients = definition.legislator_contact?.custom_recipients || [];
  const selectedCommittees = definition.legislator_contact?.committees || [];

  const isRecipientTypeEnabled = (rt: RecipientType) => recipientTypes.includes(rt);

  const canEnableRecipientType = (rt: RecipientType): boolean => {
    const opt = RECIPIENT_TYPE_OPTIONS.find(o => o.value === rt);
    if (!opt?.requirement) return true;
    if (opt.requirement === 'district') return hasDistrict;
    if (opt.requirement === 'bill') return hasBillLink;
    if (opt.requirement === 'candidate') return hasCandidateLink;
    return true;
  };

  const getRequirementHint = (rt: RecipientType): string | null => {
    const opt = RECIPIENT_TYPE_OPTIONS.find(o => o.value === rt);
    if (!opt?.requirement) return null;
    if (opt.requirement === 'district' && !hasDistrict) return 'Requires District in Demographics';
    if (opt.requirement === 'bill' && !hasBillLink) return 'Requires Related Bill in Basic Info';
    if (opt.requirement === 'candidate' && !hasCandidateLink) return 'Requires Related Candidate in Basic Info';
    return null;
  };

  const toggleRecipientType = (rt: RecipientType) => {
    const current = [...recipientTypes];
    const idx = current.indexOf(rt);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(rt);
    }
    onChange({
      ...definition,
      legislator_contact: {
        ...definition.legislator_contact!,
        recipient_types: current,
      }
    });
  };

  // Committee picker — the full list is small (~40 rows), so load it once and filter locally
  const [committees, setCommittees] = useState<CommitteeOption[]>([]);
  const [committeesLoading, setCommitteesLoading] = useState(false);
  const [committeesError, setCommitteesError] = useState('');
  const [committeeFilter, setCommitteeFilter] = useState('');
  const committeesEnabled = recipientTypes.includes('committee');

  // Guarded by a ref, not by `committeesLoading`. Putting the loading flag in both
  // the guard and the dependency array deadlocked the spinner: setting it to true
  // re-ran the effect, which ran the previous run's cleanup (cancelled = true), so
  // the in-flight response was discarded and loading was never cleared.
  const committeesRequestedRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const loadCommittees = useCallback(async () => {
    committeesRequestedRef.current = true;
    setCommitteesLoading(true);
    setCommitteesError('');
    try {
      const response = await api.get('/outreach-unified/committees');
      if (!mountedRef.current) return;
      setCommittees(response.data?.data?.committees || []);
    } catch {
      if (!mountedRef.current) return;
      // Allow a retry — otherwise a single failure strands the picker for good.
      committeesRequestedRef.current = false;
      setCommitteesError('Could not load committees.');
    } finally {
      if (mountedRef.current) setCommitteesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (committeesEnabled && !committeesRequestedRef.current) loadCommittees();
  }, [committeesEnabled, loadCommittees]);

  const toggleCommittee = (c: CommitteeOption) => {
    const already = selectedCommittees.some(sc => sc.id === c.id);
    if (!already && selectedCommittees.length >= MAX_COMMITTEES) return;
    const next: SelectedCommittee[] = already
      ? selectedCommittees.filter(sc => sc.id !== c.id)
      : [...selectedCommittees, { id: c.id, name: c.name, chamber: c.chamber }];
    onChange({
      ...definition,
      legislator_contact: {
        ...definition.legislator_contact!,
        committees: next,
      }
    });
  };

  const filteredCommittees = committees.filter(c => {
    const q = committeeFilter.trim().toLowerCase();
    if (!q) return true;
    return c.name.toLowerCase().includes(q) || (c.chamber || '').toLowerCase().includes(q);
  });

  // Custom distribution search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CustomRecipient[]>([]);
  const [searching, setSearching] = useState(false);

  // Manual entry
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualError, setManualError] = useState('');

  const searchRecipients = useCallback(async (q: string) => {
    if (q.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await api.get('/outreach-unified/recipient-search', {
        params: { q, types: 'legislators,candidates,officeholders' }
      });
      setSearchResults(response.data?.data?.results || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => searchRecipients(searchQuery), 300);
    return () => clearTimeout(timeout);
  }, [searchQuery, searchRecipients]);

  const addCustomRecipient = (r: CustomRecipient) => {
    if (customRecipients.some(cr => cr.id === r.id && cr.source_type === r.source_type)) return;
    onChange({
      ...definition,
      legislator_contact: {
        ...definition.legislator_contact!,
        custom_recipients: [...customRecipients, r],
      }
    });
    setSearchQuery('');
    setSearchResults([]);
  };

  const removeCustomRecipient = (id: string, sourceType: string) => {
    onChange({
      ...definition,
      legislator_contact: {
        ...definition.legislator_contact!,
        custom_recipients: customRecipients.filter(cr => !(cr.id === id && cr.source_type === sourceType)),
      }
    });
  };

  const addManualRecipient = () => {
    setManualError('');
    const name = manualName.trim();
    const email = manualEmail.trim().toLowerCase();
    if (!name) { setManualError('Name is required'); return; }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setManualError('Valid email is required'); return; }
    if (customRecipients.some(cr => cr.email.toLowerCase() === email)) { setManualError('This email is already in the list'); return; }
    const id = `manual_${Date.now()}`;
    addCustomRecipient({ source_type: 'manual', id, name, email });
    setManualName('');
    setManualEmail('');
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-2">
          Configure Settings
        </h2>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          Customize the experience for your respondents
        </p>
      </div>

      {/* Post-submission Message */}
      <div className="card p-6">
        <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-4">
          Post-Submission Message
        </label>
        <RichTextEditor
          value={definition.post_submission_message}
          onChange={(value) => onChange({
            ...definition,
            post_submission_message: value
          })}
          placeholder="Thank you for your response!"
          maxLength={1800}
        />
        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-2">
          This message will be shown after someone submits their response
        </p>
      </div>

      {/* Share Results Link */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            id="shareResultsLink"
            checked={definition.share_results_link || false}
            onChange={(e) => onChange({
              ...definition,
              share_results_link: e.target.checked
            })}
            className="rounded"
          />
          <label htmlFor="shareResultsLink" className="font-medium text-text-primary dark:text-text-dark-primary cursor-pointer">
            Share link to real-time results
          </label>
          <HelpTrigger contentKey="outreach-results-link" size="sm" />
        </div>
        {definition.share_results_link && (
          <div className="space-y-2 pl-6">
            <div className="flex items-start gap-2">
              <BarChart3 className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                After submission, respondents will see a link to view live, aggregated results
              </p>
            </div>
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Requires an Outreach Report marked as &ldquo;Public View&rdquo; in the Reports &amp; Communications tab. If no public report exists, the results link will not appear to respondents.
            </p>
          </div>
        )}
      </div>

      {/* Legislator Contact */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            id="enableLegislator"
            checked={definition.legislator_contact?.enabled || false}
            onChange={(e) => {
              const enabling = e.target.checked;
              const existing = definition.legislator_contact;
              onChange({
                ...definition,
                legislator_contact: {
                  ...existing,
                  enabled: enabling,
                  recipient_types: existing?.recipient_types || (hasDistrict ? ['house_rep', 'senate_rep'] : []),
                  email_template: existing?.email_template || {
                    ...DEFAULT_EMAIL_TEMPLATE,
                    included_question_ids: definition.questions.map(q => q.id),
                  },
                }
              });
            }}
            className="rounded"
          />
          <label htmlFor="enableLegislator" className="font-medium text-text-primary dark:text-text-dark-primary cursor-pointer">
            <Mail className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
            Enable one-click contact
          </label>
          <HelpTrigger contentKey="outreach-legislator-contact" size="sm" />
        </div>

        {definition.legislator_contact?.enabled && (
          <div className="space-y-5 mt-2">
            {/* ── Email Recipients Card ── */}
            <div className="card p-6">
              <label className="text-sm font-semibold text-text-primary dark:text-text-dark-primary block mb-1">
                Email Recipients
              </label>
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-4">
                Select who should receive the email.
              </p>

              <div className="space-y-1">
                {RECIPIENT_TYPE_OPTIONS.map(opt => {
                  const canEnable = canEnableRecipientType(opt.value);
                  const hint = getRequirementHint(opt.value);
                  const Icon = opt.icon;
                  return (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                        canEnable ? 'cursor-pointer hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary' : 'opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isRecipientTypeEnabled(opt.value)}
                        disabled={!canEnable}
                        onChange={() => canEnable && toggleRecipientType(opt.value)}
                        className="rounded flex-shrink-0"
                      />
                      <Icon className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                          {opt.label}
                        </span>
                        <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary ml-2">
                          &mdash; {opt.description}
                        </span>
                        {hint && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                            {hint}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              {recipientTypes.length > 1 && (
                <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-3 pt-3 border-t border-border-light dark:border-border-dark">
                  With multiple recipients, <code className="bg-background-tertiary dark:bg-background-dark-tertiary px-1 py-0.5 rounded">{'{{legislator.name}}'}</code> defaults to &ldquo;Dear Elected Official&rdquo;
                </p>
              )}

              {/* Committee picker (inline when enabled) */}
              {committeesEnabled && (
                <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
                  <label className="text-sm font-medium text-text-primary dark:text-text-dark-primary block mb-1">
                    Committees
                  </label>
                  <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-3">
                    Everyone on the committees you pick receives the email. Membership is looked up
                    when a respondent opens the page, so roster changes are picked up automatically.
                  </p>

                  {committeesLoading && (
                    <div className="flex items-center gap-2 text-sm text-text-tertiary dark:text-text-dark-tertiary mb-3">
                      <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                      Loading committees&hellip;
                    </div>
                  )}

                  {committeesError && (
                    <div className="flex items-center gap-2 mb-3">
                      <p className="text-xs text-error-600">{committeesError}</p>
                      <button
                        type="button"
                        onClick={loadCommittees}
                        className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  {!committeesLoading && !committeesError && committees.length > 0 && (
                    <>
                      <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                        <input
                          type="text"
                          value={committeeFilter}
                          onChange={(e) => setCommitteeFilter(e.target.value)}
                          placeholder="Filter committees..."
                          className="input w-full pl-9 text-sm"
                        />
                      </div>

                      <div className="border border-border-light dark:border-border-dark rounded-lg max-h-64 overflow-y-auto mb-3">
                        {filteredCommittees.length === 0 && (
                          <p className="px-3 py-2 text-sm text-text-tertiary dark:text-text-dark-tertiary italic">
                            No committees match &ldquo;{committeeFilter}&rdquo;.
                          </p>
                        )}
                        {filteredCommittees.map(c => {
                          const checked = selectedCommittees.some(sc => sc.id === c.id);
                          const atLimit = !checked && selectedCommittees.length >= MAX_COMMITTEES;
                          return (
                            <label
                              key={c.id}
                              className={`flex items-center gap-3 px-3 py-2 text-sm border-b last:border-b-0 border-border-light dark:border-border-dark ${
                                atLimit
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'cursor-pointer hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={atLimit}
                                onChange={() => toggleCommittee(c)}
                                className="rounded flex-shrink-0"
                              />
                              <span className="flex-1 min-w-0">
                                <span className="font-medium text-text-primary dark:text-text-dark-primary">{c.name}</span>
                                {c.chamber && (
                                  <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary ml-2">
                                    {c.chamber}
                                  </span>
                                )}
                              </span>
                              <span className={`text-xs whitespace-nowrap ${
                                c.contactable_count === 0
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-text-tertiary dark:text-text-dark-tertiary'
                              }`}>
                                {c.contactable_count === 0
                                  ? 'No emails on file'
                                  : `${c.contactable_count} contactable`}
                              </span>
                            </label>
                          );
                        })}
                      </div>

                      {selectedCommittees.length >= MAX_COMMITTEES && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
                          Maximum of {MAX_COMMITTEES} committees selected.
                        </p>
                      )}
                    </>
                  )}

                  {selectedCommittees.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedCommittees.map(sc => (
                        <div
                          key={sc.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
                        >
                          <span className="font-medium">{sc.name}</span>
                          {sc.chamber && <span className="text-primary-400">({sc.chamber})</span>}
                          <button
                            type="button"
                            onClick={() => toggleCommittee({ ...sc, member_count: 0, contactable_count: 0 })}
                            className="ml-0.5 hover:text-error-600"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedCommittees.length === 0 && !committeesLoading && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      No committees selected yet — this recipient source will add no one.
                    </p>
                  )}
                </div>
              )}

              {/* Custom Distribution Search (inline when enabled) */}
              {isRecipientTypeEnabled('custom') && (
                <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
                  <label className="text-sm font-medium text-text-primary dark:text-text-dark-primary block mb-1">
                    Custom Recipients
                  </label>
                  <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-3">
                    Search for existing people or manually add any email address
                  </p>

                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name..."
                      className="input w-full pl-9 text-sm"
                    />
                    {searching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>

                  {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                    <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-3 italic">
                      No results found for &ldquo;{searchQuery}&rdquo;. Only people with email addresses on file are shown.
                    </p>
                  )}

                  {searchResults.length > 0 && (
                    <div className="border border-border-light dark:border-border-dark rounded-lg max-h-48 overflow-y-auto mb-3">
                      {searchResults.map((r) => {
                        const alreadyAdded = customRecipients.some(cr => cr.id === r.id && cr.source_type === r.source_type);
                        return (
                          <button
                            key={`${r.source_type}-${r.id}`}
                            type="button"
                            disabled={alreadyAdded}
                            onClick={() => addCustomRecipient(r)}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between border-b last:border-b-0 border-border-light dark:border-border-dark ${
                              alreadyAdded
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary cursor-pointer'
                            }`}
                          >
                            <div>
                              <span className="font-medium text-text-primary dark:text-text-dark-primary">{r.name}</span>
                              <span className="text-text-tertiary dark:text-text-dark-tertiary ml-2">{r.email}</span>
                              {r.email_context && (
                                <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary ml-1">({r.email_context})</span>
                              )}
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-background-tertiary dark:bg-background-dark-tertiary text-text-tertiary">
                              {r.source_type}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Manual entry */}
                  <div className="flex items-start gap-2 mb-3">
                    <input
                      type="text"
                      value={manualName}
                      onChange={(e) => { setManualName(e.target.value); setManualError(''); }}
                      placeholder="Name"
                      className="input flex-1 text-sm"
                    />
                    <input
                      type="email"
                      value={manualEmail}
                      onChange={(e) => { setManualEmail(e.target.value); setManualError(''); }}
                      placeholder="Email address"
                      className="input flex-1 text-sm"
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addManualRecipient(); } }}
                    />
                    <button
                      type="button"
                      onClick={addManualRecipient}
                      className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors whitespace-nowrap"
                    >
                      <Plus className="w-4 h-4" />
                      Add
                    </button>
                  </div>
                  {manualError && (
                    <p className="text-xs text-error-600 mb-3">{manualError}</p>
                  )}

                  {customRecipients.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {customRecipients.map((cr) => (
                        <div
                          key={`${cr.source_type}-${cr.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
                        >
                          <span className="font-medium">{cr.name}</span>
                          <span className="text-primary-400">({cr.email})</span>
                          <button
                            type="button"
                            onClick={() => removeCustomRecipient(cr.id, cr.source_type)}
                            className="ml-0.5 hover:text-error-600"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Recipient Visibility Card ── */}
            <div className="card p-6">
              <label className="text-sm font-semibold text-text-primary dark:text-text-dark-primary block mb-1">
                Recipient Visibility
              </label>
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-4">
                Choose whether recipients of a distribution can see each other on the email.
              </p>
              <div className="space-y-2">
                <label className="flex items-start gap-3 px-3 py-2.5 rounded-md cursor-pointer hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary">
                  <input
                    type="radio"
                    name="recipientVisibility"
                    checked={(definition.legislator_contact.recipient_visibility || 'bcc') === 'bcc'}
                    onChange={() => onChange({
                      ...definition,
                      legislator_contact: {
                        ...definition.legislator_contact!,
                        recipient_visibility: 'bcc',
                      }
                    })}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                      Hidden (BCC)
                    </span>
                    <span className="block text-xs text-text-tertiary dark:text-text-dark-tertiary">
                      Recipients cannot see each other. The respondent&apos;s own email is used as the To address when available.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3 px-3 py-2.5 rounded-md cursor-pointer hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary">
                  <input
                    type="radio"
                    name="recipientVisibility"
                    checked={definition.legislator_contact.recipient_visibility === 'to'}
                    onChange={() => onChange({
                      ...definition,
                      legislator_contact: {
                        ...definition.legislator_contact!,
                        recipient_visibility: 'to',
                      }
                    })}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                      Visible (To)
                    </span>
                    <span className="block text-xs text-text-tertiary dark:text-text-dark-tertiary">
                      All recipients appear in the To line and can see each other.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {/* ── Respondent Details Card ── */}
            <div className="card p-6">
              <div className="flex items-center gap-3 mb-1">
                <input
                  type="checkbox"
                  id="includeRespondentInfo"
                  checked={definition.legislator_contact.include_respondent_info || false}
                  onChange={(e) => onChange({
                    ...definition,
                    legislator_contact: {
                      ...definition.legislator_contact!,
                      include_respondent_info: e.target.checked,
                      respondent_info_fields: definition.legislator_contact!.respondent_info_fields
                        || ['name', 'address', 'district'],
                    }
                  })}
                  className="rounded"
                />
                <label htmlFor="includeRespondentInfo" className="text-sm font-semibold text-text-primary dark:text-text-dark-primary cursor-pointer">
                  Include respondent details in the email
                </label>
              </div>
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-4">
                Adds an &ldquo;About the respondent&rdquo; block so recipients can verify who is contacting them. Only fields the respondent filled in are included.
              </p>
              {definition.legislator_contact.include_respondent_info && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {RESPONDENT_INFO_FIELD_OPTIONS.map(opt => {
                    const selected = (definition.legislator_contact!.respondent_info_fields || []).includes(opt.value);
                    return (
                      <label key={opt.value} className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const current = definition.legislator_contact!.respondent_info_fields || [];
                            onChange({
                              ...definition,
                              legislator_contact: {
                                ...definition.legislator_contact!,
                                respondent_info_fields: e.target.checked
                                  ? [...current, opt.value]
                                  : current.filter(f => f !== opt.value),
                              }
                            });
                          }}
                          className="rounded"
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Email Template Card ── */}
            {definition.legislator_contact.email_template && (() => {
              const et = definition.legislator_contact.email_template!;
              const updateTemplate = (patch: Partial<typeof et>) => onChange({
                ...definition,
                legislator_contact: {
                  ...definition.legislator_contact!,
                  email_template: { ...et, ...patch },
                }
              });

              return (
                <div className="card p-6">
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-sm font-semibold text-text-primary dark:text-text-dark-primary block">
                      Email Template
                    </label>
                    <HelpTrigger contentKey="outreach-email-template" size="sm" />
                  </div>
                  <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-5">
                    Configure the email sections respondents will send. Use placeholders like{' '}
                    <code className="bg-background-tertiary dark:bg-background-dark-tertiary px-1 py-0.5 rounded">{'{{recipient.name}}'}</code>,{' '}
                    <code className="bg-background-tertiary dark:bg-background-dark-tertiary px-1 py-0.5 rounded">{'{{bill.number}}'}</code>,{' '}
                    <code className="bg-background-tertiary dark:bg-background-dark-tertiary px-1 py-0.5 rounded">{'{{respondent.first_name}}'}</code>
                  </p>

                  <div className="space-y-5">
                    {/* Subject */}
                    <div>
                      <label className="text-sm font-medium text-text-primary dark:text-text-dark-primary block mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        value={et.subject.content}
                        onChange={(e) => updateTemplate({ subject: { ...et.subject, content: e.target.value } })}
                        className="input w-full text-sm"
                        placeholder="Constituent Feedback on {{bill.number}}"
                      />
                    </div>

                    <hr className="border-border-light dark:border-border-dark" />

                    {/* Opening Text */}
                    <div>
                      <label className="text-sm font-medium text-text-primary dark:text-text-dark-primary block mb-2">
                        Opening Text
                      </label>
                      <textarea
                        value={et.opening.content}
                        onChange={(e) => updateTemplate({ opening: { ...et.opening, content: e.target.value } })}
                        className="input w-full text-sm"
                        rows={3}
                        placeholder="Dear {{legislator.name}},&#10;&#10;I am writing as your constituent..."
                      />
                    </div>

                    {/* Include Questions */}
                    {definition.questions.length > 0 && (
                      <>
                        <hr className="border-border-light dark:border-border-dark" />
                        <div>
                          <label className="text-sm font-medium text-text-primary dark:text-text-dark-primary block mb-1">
                            Include Question Answers
                          </label>
                          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-3">
                            Selected question answers will be included in the email body (not editable by respondent)
                          </p>
                          <div className="space-y-2">
                            {definition.questions.map((q) => (
                              <label key={q.id} className="flex items-start gap-2 text-sm text-text-secondary dark:text-text-dark-secondary cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={et.included_question_ids?.includes(q.id) || false}
                                  onChange={(e) => {
                                    const ids = et.included_question_ids || [];
                                    updateTemplate({
                                      included_question_ids: e.target.checked
                                        ? [...ids, q.id]
                                        : ids.filter(id => id !== q.id),
                                    });
                                  }}
                                  className="rounded mt-0.5"
                                />
                                <span>{q.question}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                    <hr className="border-border-light dark:border-border-dark" />

                    {/* Respondent Free Text */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id="freeTextEnabled"
                          checked={et.free_text_enabled}
                          onChange={(e) => updateTemplate({ free_text_enabled: e.target.checked })}
                          className="rounded"
                        />
                        <label htmlFor="freeTextEnabled" className="text-sm font-medium text-text-primary dark:text-text-dark-primary cursor-pointer">
                          Respondent Free Text Block
                        </label>
                      </div>
                      {et.free_text_enabled && (
                        <input
                          type="text"
                          value={et.free_text_placeholder || ''}
                          onChange={(e) => updateTemplate({ free_text_placeholder: e.target.value })}
                          className="input w-full text-sm mt-2"
                          placeholder="Placeholder text shown to respondent..."
                        />
                      )}
                    </div>

                    <hr className="border-border-light dark:border-border-dark" />

                    {/* Closing Text */}
                    <div>
                      <label className="text-sm font-medium text-text-primary dark:text-text-dark-primary block mb-2">
                        Closing Text
                      </label>
                      <textarea
                        value={et.closing.content}
                        onChange={(e) => updateTemplate({ closing: { ...et.closing, content: e.target.value } })}
                        className="input w-full text-sm"
                        rows={3}
                        placeholder="Thank you for your consideration.&#10;&#10;Sincerely,&#10;{{respondent.first_name}} {{respondent.last_name}}"
                      />
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

    </div>
  );
};

export default WizardStep4Settings;
